// src/components/kuestion/campus/CohortAnalytics.jsx
//
// Strength by module across the whole organisation (contract §7). Below the
// server's `min_questions_for_strength` a module is reported as insufficient
// data, never as a misleading percentage. The threshold comes from the
// response — nothing here hardcodes it.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { Loading, Alert, RuleHead, ModuleRow } from '../shared/Bits';
import { fullName } from '../shared/format';
import { kq } from '../../../config/routes';

export function ReportsPanel() {
  const { reports } = useCampus();
  return (
    <div>
      <RuleHead title="Reports" />
      {reports.length === 0 ? (
        <div className="kqc-empty" style={{ borderTop: 0 }}>No PDFs requested yet. Build one from a student report or export a module below.</div>
      ) : (
        <div className="kqc-box kqc-reports">
          {reports.map((r) => (
            <div key={r.key}>
              <span className={`dot${r.status === 'building' || r.status === 'queued' ? ' is-building' : ''}`} />
              <span className="body">
                <strong>{r.label}</strong>
                <em>{r.status === 'queued' ? 'Queued — it appears here when it is ready' : r.status === 'building' ? 'Building — we’ll tell you when it’s ready' : r.status === 'ready' ? 'Ready' : r.error || 'Failed'}</em>
              </span>
              {r.status === 'ready' && r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="kqc-mono" style={{ fontSize: 11 }}>Download</a>}
            </div>
          ))}
        </div>
      )}
      <p className="kqc-small kqc-muted" style={{ marginTop: 12 }}>PDFs are built in the background. Leave the page — they stay in this list while you are signed in.</p>
    </div>
  );
}

export default function CohortAnalytics() {
  const navigate = useNavigate();
  const { api, students, requestReport } = useCampus();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [mod, setMod] = useState('');

  useEffect(() => {
    let live = true;
    api.get(API_ENDPOINTS.CAMPUS_ANALYTICS_COHORT)
      .then((d) => { if (live) setData(d || { modules: [] }); })
      .catch((e) => { if (live) { setError(e.message); setData({ modules: [] }); } });
    return () => { live = false; };
  }, [api]);

  const modules = useMemo(() => data?.modules || [], [data]);
  const min = data?.min_questions_for_strength;
  const byId = useMemo(() => new Map(students.map((s) => [String(s.id), s])), [students]);

  // "Weakest first" — from the server when it sends per-student rows.
  const weakest = useMemo(() => {
    const rows = data?.students || data?.weakest || [];
    return rows.map((r) => ({
      id: r.student_id ?? r.id,
      name: r.name || (r.first_name || r.last_name ? fullName(r) : '') || fullName(byId.get(String(r.student_id ?? r.id))) || r.login_id || '—',
      module: r.weakest_module || r.module || '—',
      strength: r.strength,
      sufficient: r.sufficient !== false && r.strength != null,
      last: r.last_result || null, // { percentage, passed } — passed may be null (no pass mark)
    })).sort((a, b) => (a.sufficient ? a.strength : 101) - (b.sufficient ? b.strength : 101));
  }, [data, byId]);

  useEffect(() => { if (!mod && modules.length) setMod(modules[0].module); }, [modules, mod]);

  return (
    <div className="kqc-page">
      <div className="kqc-pagehead" style={{ marginBottom: 10 }}>
        <div>
          <div className="kqc-kicker" style={{ marginBottom: 8 }}>Analytics · whole organisation</div>
          <h1 className="kqc-h1">Strength by module</h1>
        </div>
        <span className="kqc-spacer" />
        {modules.length > 0 && (
          <div className="kqc-row" style={{ gap: 8 }}>
            <select className="input" style={{ minHeight: 40, width: 'auto', minWidth: 180 }} value={mod} onChange={(e) => setMod(e.target.value)} aria-label="Module to export">
              {modules.map((m) => <option key={m.module} value={m.module}>{m.module}</option>)}
            </select>
            <button type="button" data-tour="coh-pdf" className="btn btn-secondary btn-sm" style={{ minHeight: 40 }} disabled={!mod} onClick={() => requestReport(`module:${mod}`, mod, API_ENDPOINTS.CAMPUS_REPORT_MODULE(mod))}>Export module PDF</button>
          </div>
        )}
      </div>
      <p className="kqc-lede" style={{ marginBottom: 24, maxWidth: '64ch', fontSize: 13 }}>
        Strength is the share of questions answered correctly within a module. Below <strong>{min ?? 'a minimum number of'} attempted questions</strong> we report insufficient data rather than a percentage. Two out of three is not 67% worth of anything, and a figure like that would send you revising the wrong thing.
      </p>

      {error && <Alert kind="error">{error}</Alert>}
      {!data ? <Loading label="Computing strengths" /> : modules.length === 0 ? (
        <div className="kqc-empty" style={{ marginBottom: 30 }}>No sittings yet. Strengths appear once students submit exams in either mode.</div>
      ) : (
        <div className="kqc-list" style={{ marginBottom: 30 }}>
          {modules.map((m) => <ModuleRow key={m.module} m={m} min={min} />)}
        </div>
      )}

      <div className="kqc-cols">
        <div className="is-main">
          <RuleHead title="Weakest first" />
          {weakest.length === 0 ? (
            <div className="kqc-empty" style={{ borderTop: 0 }}>Per-student rows appear here when the cohort response includes them. Open any student&rsquo;s report from the roster in the meantime.</div>
          ) : (
            <div className="table-wrap">
              <table className="table" style={{ minWidth: 420 }}>
                <thead><tr><th>Student</th><th>Weakest module</th><th>Strength</th><th>Last result</th><th /></tr></thead>
                <tbody>
                  {weakest.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{r.module}</td>
                      <td>{r.sufficient ? <strong className={Number(r.strength) < 50 ? 'kqc-accent-ink' : ''}>{Math.round(Number(r.strength))}%</strong> : <span className="kqc-muted">Insufficient data</span>}</td>
                      <td>
                        {r.last && r.last.percentage != null ? (
                          <span className="kqc-row" style={{ gap: 8, display: 'inline-flex' }}>
                            <strong>{Math.round(Number(r.last.percentage))}%</strong>
                            {r.last.passed != null && <span className={`kqc-verdict is-${r.last.passed ? 'pass' : 'fail'}`}>{r.last.passed ? 'Pass' : 'Fail'}</span>}
                          </span>
                        ) : <span className="kqc-muted">—</span>}
                      </td>
                      <td className="is-right"><button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 0, display: 'inline-flex' }} onClick={() => navigate(kq(`analytics/students/${r.id}`))}>Report</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="is-side" style={{ maxWidth: 360 }}>
          <ReportsPanel />
        </div>
      </div>
    </div>
  );
}
